/**
 * Biblioteca de reportes maestros (grid/lista y semáforo de aprobación).
 */
import React, { useEffect, useState } from 'react';
import ReportForm from './ReportForm';
import ChartLineIcon from '../../assets/icons/chart-line.svg';
import EditIcon from '../../assets/icons/edit.svg';
import ClipboardListIcon from '../../assets/icons/clipboard-list.svg';
import GoogleDriveIcon from '../../assets/icons/google-drive.svg';
import UsersIcon from '../../assets/icons/users.svg';
import TrashIcon from '../../assets/icons/trash.svg';
import DetailsIcon from '../../assets/icons/info.svg';
import CopyIcon from '../../assets/icons/copy.svg';
import { ink, studio, CatalogCard, StudioIcon, SearchField, FilterDock, FilterPill, FilterMeta } from './studioUi';
import { deadlineTone } from '../../datetimeLocal';

const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function approvalInfo(report) {
    if (!report.totalResponsibles) return { color: ink.lineStrong, text: '—' };
    const pct = (report.approvedCount / report.totalResponsibles) * 100;
    let color = ink.danger;
    let text = `${report.approvedCount} / ${report.totalResponsibles}`;
    if (report.approvalStatus === 'complete_late' || (pct === 100 && report.approvalStatus === 'complete_late')) {
        color = '#e65100';
        text = `Retrasos · ${text}`;
    } else if (pct === 100) {
        color = '#137333';
    } else if (pct > 0) {
        color = '#e37400';
    }
    return { color, text };
}

function adjustmentInfo(report) {
    const counts = report.adjustmentCounts || {};
    const pending = Number(counts.pending) || 0;
    const completed = Number(counts.completed) || 0;
    return { pending, completed, open: pending + completed };
}

const dueChip = {
    muted: { backgroundColor: ink.wash, color: ink.faint },
    ok: { backgroundColor: ink.wash, color: ink.muted },
    warn: { backgroundColor: '#fff3e0', color: '#e65100' },
    danger: { backgroundColor: ink.dangerSoft, color: ink.danger },
};

const AdjustmentBits = ({ report }) => {
    const bits = adjustmentInfo(report);
    if (!bits.open) return <span style={{ color: ink.faint }}>—</span>;
    return (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            {bits.pending > 0 && (
                <span style={{ ...chip.meta, backgroundColor: '#fff3e0', color: '#e65100' }}>{bits.pending} pend.</span>
            )}
            {bits.completed > 0 && (
                <span style={{ ...chip.meta, backgroundColor: ink.accentSoft, color: ink.accentText }}>{bits.completed} por validar</span>
            )}
        </span>
    );
};

const ReportLibrary = ({
    biblioteca,
    clients,
    projects,
    cargarTodo,
    handleShowReportViews,
    handleShowAccessUsers,
    styles,
    userPermissions,
    onViewDetails,
    onEditReport,
    onDeleteReport,
    onFinishEditing,
    onSaveReport,
    editandoId,
    nuevoMaestro,
    setNuevoMaestro,
    handleShowResponsiblesModal,
    onCopyReport,
}) => {
    const [showReportForm, setShowReportForm] = useState(false);
    const [filtroCliente, setFiltroCliente] = useState('');
    const [filtroProyecto, setFiltroProyecto] = useState('');
    const [filtroNombre, setFiltroNombre] = useState('');
    const [filtroEnfoque, setFiltroEnfoque] = useState('');
    const [filtroVersionYear, setFiltroVersionYear] = useState('');
    const [filtroVersionMonth, setFiltroVersionMonth] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [hoveredRow, setHoveredRow] = useState(null);

    useEffect(() => {
        cargarTodo();
        const handleFocus = () => cargarTodo();
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [cargarTodo]);

    useEffect(() => {
        if (editandoId) {
            setShowReportForm(true);
        } else if (nuevoMaestro.id === '' && nuevoMaestro.reporteNombre === '') {
            setShowReportForm(false);
        }
    }, [editandoId, nuevoMaestro]);

    const reportesFiltrados = biblioteca.filter((r) => {
        const matchCliente = filtroCliente ? r.clientId === filtroCliente : true;
        const matchProyecto = filtroProyecto ? r.projectId === filtroProyecto : true;
        const matchNombre = filtroNombre ? r.reporteNombre.toLowerCase().includes(filtroNombre.toLowerCase()) : true;
        const matchEnfoque = filtroEnfoque ? (r.enfoque || 'Interno') === filtroEnfoque : true;
        const matchVersionYear = filtroVersionYear ? r.versionYear === filtroVersionYear : true;
        const matchVersionMonth = filtroVersionMonth ? String(r.versionMonth) === filtroVersionMonth : true;
        return matchCliente && matchProyecto && matchNombre && matchEnfoque && matchVersionYear && matchVersionMonth;
    });

    const projectsForFilter = filtroCliente
        ? projects.filter((p) => p.clientId === filtroCliente)
        : projects;

    const uniqueYears = Array.from(new Set(biblioteca.map((r) => r.versionYear).filter(Boolean))).sort((a, b) => b - a);

    const projectCounts = projects.reduce((acc, project) => {
        if (project.clientId) acc[project.clientId] = (acc[project.clientId] || 0) + 1;
        return acc;
    }, {});

    const reportCounts = biblioteca.reduce((acc, report) => {
        if (report.projectId) acc[report.projectId] = (acc[report.projectId] || 0) + 1;
        return acc;
    }, {});

    const reportsForSelectedProject = selectedProject
        ? reportesFiltrados.filter((r) => r.projectId === selectedProject.id)
        : [];

    const clearFilters = () => {
        setFiltroCliente('');
        setFiltroProyecto('');
        setFiltroNombre('');
        setFiltroEnfoque('');
        setFiltroVersionYear('');
        setFiltroVersionMonth('');
    };

    const clientName = (id) => clients.find((c) => c.id === id)?.nombre || id;
    const projectName = (id) => projects.find((p) => p.id === id)?.nombre || id;

    const activeChips = [
        filtroNombre && { key: 'nombre', label: `“${filtroNombre}”`, onClear: () => setFiltroNombre('') },
        filtroCliente && { key: 'cliente', label: clientName(filtroCliente), onClear: () => { setFiltroCliente(''); setFiltroProyecto(''); } },
        filtroProyecto && { key: 'proyecto', label: projectName(filtroProyecto), onClear: () => setFiltroProyecto('') },
        filtroEnfoque && { key: 'enfoque', label: filtroEnfoque, onClear: () => setFiltroEnfoque('') },
        filtroVersionYear && { key: 'year', label: String(filtroVersionYear), onClear: () => setFiltroVersionYear('') },
        filtroVersionMonth && { key: 'month', label: monthNames[Number(filtroVersionMonth) - 1], onClear: () => setFiltroVersionMonth('') },
    ].filter(Boolean);

    const hasFilters = activeChips.length > 0;
    const matchCount = selectedProject ? reportsForSelectedProject.length : reportesFiltrados.length;

    const goClients = () => {
        setSelectedClient(null);
        setSelectedProject(null);
    };

    const renderLinkBtn = (href, title, icon, alt) => (
        href ? (
            <a title={title} href={href} target="_blank" rel="noopener noreferrer" style={studio.iconBtn}>
                <StudioIcon src={icon} alt={alt} />
            </a>
        ) : null
    );

    const reportActions = (report, compact) => (
        <>
            <button type="button" title="Ver detalles" onClick={() => onViewDetails(report)} style={studio.iconBtn}>
                <StudioIcon src={DetailsIcon} alt="Detalles" />
            </button>
            {userPermissions.reports_create && (
                <button type="button" title="Copiar reporte" onClick={() => onCopyReport(report)} style={studio.iconBtn}>
                    <StudioIcon src={CopyIcon} alt="Copiar" />
                </button>
            )}
            {userPermissions.reports_edit && (
                <button type="button" title="Editar reporte" onClick={() => onEditReport(report)} style={studio.iconBtn}>
                    <StudioIcon src={EditIcon} alt="Editar" />
                </button>
            )}
            {userPermissions.reports_delete && (
                <button type="button" title="Borrar reporte" onClick={() => onDeleteReport(report.id)} style={studio.iconBtnDanger}>
                    <StudioIcon src={TrashIcon} alt="Eliminar" />
                </button>
            )}
            {!compact && userPermissions.reports_view_access_users && (
                <button type="button" title="Ver usuarios con acceso" onClick={() => handleShowAccessUsers(report)} style={studio.iconBtn}>
                    <StudioIcon src={UsersIcon} alt="Usuarios" />
                </button>
            )}
            {!compact && userPermissions.reports_view_responsibles && (
                <button type="button" title="Gestionar responsables" onClick={() => handleShowResponsiblesModal(report)} style={studio.iconBtn}>
                    <StudioIcon src={ClipboardListIcon} alt="Responsables" />
                </button>
            )}
        </>
    );

    return (
        <div style={studio.page}>
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Catálogo</p>
                    <h2 style={studio.title}>Biblioteca de reportes</h2>
                    <p style={studio.subtitle}>Explora por cliente y proyecto. Semáforo de aprobación, deadline y ajustes abiertos.</p>
                </div>
                <div style={studio.stats}>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{biblioteca.length}</span>
                        <span style={studio.statLabel}>Reportes</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{clients.length}</span>
                        <span style={studio.statLabel}>Clientes</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{projects.length}</span>
                        <span style={studio.statLabel}>Proyectos</span>
                    </div>
                </div>
            </div>

            <div style={studio.card}>
                <div style={studio.toolbar}>
                    <h3 style={{ ...studio.formTitle, margin: 0 }}>
                        {selectedProject ? selectedProject.nombre : selectedClient ? selectedClient.nombre : 'Explorar'}
                    </h3>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        {selectedProject && (
                            <div style={studio.scopeBar}>
                                <button type="button" onClick={() => setViewMode('list')} style={viewMode === 'list' ? studio.scopeOn : studio.scopeOff}>Lista</button>
                                <button type="button" onClick={() => setViewMode('grid')} style={viewMode === 'grid' ? studio.scopeOn : studio.scopeOff}>Cuadrícula</button>
                            </div>
                        )}
                        {userPermissions.reports_create && !showReportForm && (
                            <button type="button" onClick={() => setShowReportForm(true)} style={studio.primaryBtn}>Nuevo reporte</button>
                        )}
                        {(selectedClient || selectedProject) && (
                            <button type="button" onClick={selectedProject ? () => setSelectedProject(null) : goClients} style={studio.ghostBtn}>Volver</button>
                        )}
                    </div>
                </div>

                <div style={studio.crumbBar}>
                    <button type="button" onClick={goClients} style={studio.crumbBtn}>Clientes</button>
                    {selectedClient && (
                        <>
                            <span style={studio.crumbSep}>/</span>
                            <button type="button" onClick={() => setSelectedProject(null)} style={studio.crumbBtn}>{selectedClient.nombre}</button>
                        </>
                    )}
                    {selectedProject && (
                        <>
                            <span style={studio.crumbSep}>/</span>
                            <span style={studio.crumbHere}>{selectedProject.nombre}</span>
                        </>
                    )}
                </div>

                {showReportForm && (
                    <div style={{ marginTop: 16 }}>
                        <ReportForm
                            styles={styles}
                            nuevoMaestro={nuevoMaestro}
                            setNuevoMaestro={setNuevoMaestro}
                            clients={clients}
                            projects={projects}
                            editandoId={editandoId}
                            onSave={onSaveReport}
                            onCancel={onFinishEditing}
                        />
                    </div>
                )}

                <FilterDock style={{ marginTop: 18 }}>
                    <SearchField
                        value={filtroNombre}
                        onChange={setFiltroNombre}
                        placeholder="Buscar reporte por nombre…"
                    />
                    <div style={studio.filterPills}>
                        <FilterPill value={filtroCliente} onChange={(value) => { setFiltroCliente(value); setFiltroProyecto(''); }} emptyLabel="Cliente">
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                        </FilterPill>
                        <FilterPill value={filtroProyecto} onChange={setFiltroProyecto} disabled={!filtroCliente} emptyLabel="Proyecto">
                            {projectsForFilter.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                        </FilterPill>
                        <FilterPill value={filtroEnfoque} onChange={setFiltroEnfoque} emptyLabel="Enfoque">
                            <option value="Interno">Interno</option>
                            <option value="Cliente">Cliente</option>
                        </FilterPill>
                        <FilterPill value={filtroVersionYear} onChange={setFiltroVersionYear} emptyLabel="Año">
                            {uniqueYears.map((year) => <option key={year} value={year}>{year}</option>)}
                        </FilterPill>
                        <FilterPill value={filtroVersionMonth} onChange={setFiltroVersionMonth} emptyLabel="Mes">
                            {monthNames.map((name, index) => (
                                <option key={index + 1} value={index + 1}>{name}</option>
                            ))}
                        </FilterPill>
                    </div>
                    <FilterMeta
                        summary={hasFilters
                            ? `${matchCount} ${matchCount === 1 ? 'resultado' : 'resultados'}`
                            : `${biblioteca.length} reportes en el catálogo`}
                        chips={activeChips}
                        onClearAll={clearFilters}
                    />
                </FilterDock>

                {selectedProject && viewMode === 'list' && (
                    reportsForSelectedProject.length === 0 ? (
                        <div style={studio.empty}>No hay reportes con esos filtros.</div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={studio.table}>
                                <thead>
                                    <tr>
                                        <th style={studio.th}>Reporte</th>
                                        <th style={studio.th}>Aprobación</th>
                                        <th style={studio.th}>Deadline</th>
                                        <th style={studio.th}>Ajustes</th>
                                        <th style={studio.th}>Versión</th>
                                        <th style={studio.th}>Enfoque</th>
                                        <th style={studio.th}>Vistas</th>
                                        <th style={studio.th}>Enlaces</th>
                                        <th style={studio.th}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportsForSelectedProject.map((r) => {
                                        const approval = approvalInfo(r);
                                        const due = deadlineTone(r.approvalDeadline);
                                        return (
                                            <tr
                                                key={r.id}
                                                style={hoveredRow === r.id ? studio.trHover : undefined}
                                                onMouseEnter={() => setHoveredRow(r.id)}
                                                onMouseLeave={() => setHoveredRow(null)}
                                            >
                                                <td style={{ ...studio.td, fontWeight: 600 }}>
                                                    <button type="button" onClick={() => onViewDetails(r)} style={studio.crumbBtn}>
                                                        {r.reporteNombre}
                                                    </button>
                                                    {r.isDraft && <span style={chip.draft}>Borrador</span>}
                                                </td>
                                                <td style={studio.td}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                        <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: approval.color, display: 'inline-block' }} />
                                                        {approval.text}
                                                    </span>
                                                </td>
                                                <td style={studio.td}>
                                                    <span style={{
                                                        borderRadius: 999,
                                                        padding: '4px 8px',
                                                        fontSize: '0.74rem',
                                                        fontWeight: 700,
                                                        whiteSpace: 'nowrap',
                                                        ...dueChip[due.tone],
                                                    }}
                                                    >
                                                        {due.label}
                                                    </span>
                                                </td>
                                                <td style={studio.td}><AdjustmentBits report={r} /></td>
                                                <td style={{ ...studio.td, color: ink.muted }}>
                                                    {r.versionYear && r.versionMonth ? `${r.versionYear} · ${monthNames[r.versionMonth - 1]}` : '—'}
                                                </td>
                                                <td style={studio.td}>{r.enfoque || 'Interno'}</td>
                                                <td style={studio.td}>
                                                    {userPermissions.reports_view_statistics && (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                            <span style={{ fontWeight: 600 }}>{r.viewCount || 0}</span>
                                                            {userPermissions.reports_view_views && (
                                                                <button type="button" title="Ver desglose de vistas" onClick={() => handleShowReportViews(r)} style={studio.iconBtn}>
                                                                    <StudioIcon src={ChartLineIcon} alt="Vistas" />
                                                                </button>
                                                            )}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ ...studio.td, whiteSpace: 'nowrap' }}>
                                                    {r.hasEditableUrl && r.editableUrl && renderLinkBtn(r.editableUrl, 'Abrir enlace editable', EditIcon, 'Editar')}
                                                    {renderLinkBtn(r.corUrl, 'Abrir tarea en COR', ClipboardListIcon, 'COR')}
                                                    {renderLinkBtn(r.repositorio, 'Abrir repositorio', GoogleDriveIcon, 'Repositorio')}
                                                </td>
                                                <td style={{ ...studio.td, whiteSpace: 'nowrap' }}>{reportActions(r, false)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
                )}

                {selectedProject && viewMode === 'grid' && (
                    reportsForSelectedProject.length === 0 ? (
                        <div style={studio.empty}>No hay reportes con esos filtros.</div>
                    ) : (
                        <div style={studio.entityGrid}>
                            {reportsForSelectedProject.map((r) => {
                                const approval = approvalInfo(r);
                                const due = deadlineTone(r.approvalDeadline);
                                return (
                                    <div key={r.id} style={studio.entityCard}>
                                        <button type="button" onClick={() => onViewDetails(r)} style={{ ...studio.entityHit, padding: 0, alignItems: 'stretch' }}>
                                            <div style={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: ink.wash, borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: ink.line }}>
                                                <img src={ChartLineIcon} alt="" style={{ width: 40, height: 40, opacity: 0.28 }} />
                                            </div>
                                            <div style={{ padding: '12px 14px 14px', textAlign: 'left' }}>
                                                <div style={{ fontWeight: 600, color: ink.text, fontSize: '0.92rem' }}>
                                                    {r.reporteNombre}{r.isDraft ? ' · Borrador' : ''}
                                                </div>
                                                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '0.8rem', color: ink.faint }}>
                                                    <span>{r.versionYear && r.versionMonth ? `${r.versionYear} · ${monthNames[r.versionMonth - 1]}` : '—'}</span>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: approval.color }} />
                                                        {approval.text}
                                                    </span>
                                                </div>
                                                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <span style={{
                                                        borderRadius: 999,
                                                        padding: '2px 8px',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700,
                                                        ...dueChip[due.tone],
                                                    }}
                                                    >
                                                        {due.label}
                                                    </span>
                                                    <AdjustmentBits report={r} />
                                                </div>
                                            </div>
                                        </button>
                                        <div style={studio.entityActions}>{reportActions(r, true)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}

                {selectedClient && !selectedProject && (
                    (projects.filter((p) => p.clientId === selectedClient.id).length === 0) ? (
                        <div style={studio.empty}>Este cliente no tiene proyectos.</div>
                    ) : (
                        <div style={studio.entityGrid}>
                            {projects.filter((p) => p.clientId === selectedClient.id).map((p) => (
                                <CatalogCard
                                    key={p.id}
                                    name={p.nombre}
                                    meta={`${reportCounts[p.id] || 0} ${(reportCounts[p.id] || 0) === 1 ? 'reporte' : 'reportes'}`}
                                    logoUrl={p.logoUrl}
                                    onOpen={() => setSelectedProject(p)}
                                />
                            ))}
                        </div>
                    )
                )}

                {!selectedClient && (
                    clients.length === 0 ? (
                        <div style={studio.empty}>Aún no hay clientes en la biblioteca.</div>
                    ) : (
                        <div style={studio.entityGrid}>
                            {clients.map((c) => (
                                <CatalogCard
                                    key={c.id}
                                    name={c.nombre}
                                    meta={`${projectCounts[c.id] || 0} ${(projectCounts[c.id] || 0) === 1 ? 'proyecto' : 'proyectos'}`}
                                    logoUrl={c.logoUrl}
                                    onOpen={() => setSelectedClient(c)}
                                />
                            ))}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

const chip = {
    draft: {
        marginLeft: 8,
        fontSize: '0.7rem',
        color: '#e65100',
        backgroundColor: '#fff3e0',
        padding: '2px 6px',
        borderRadius: 999,
        fontWeight: 700,
    },
    meta: {
        borderRadius: 999,
        padding: '2px 7px',
        fontSize: '0.7rem',
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },
};

export default ReportLibrary;

